import React from 'react';
import {cn} from '@chekin/guestapp-ui-kit';
import {Options} from 'react-select';
import {LocationType, SelectOption, isNumeric} from '@chekin/core';
import {SelectRefType} from '@guestapp/old-ui-kit';
import {customCountriesFilter, useFetchLocations} from '@chekin/shared';
import {SearchingSelect} from '../../SearchingSelect';

export type CountryCodeOption = {
  name: string;
  code: string;
};

export type PhoneCodesSelectProps = {
  value?: CountryCodeOption | null;
  onChange: (option: CountryCodeOption) => void;
  defaultCode?: string;
  readOnly?: boolean;
  disabled?: boolean;
  error?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

type PhoneCodeOption = SelectOption<LocationType | undefined>;

const getPhoneOption = (option: PhoneCodeOption) => {
  if (option.data && !isNumeric(option?.value)) {
    const phoneCodeValue = `+${option.data.phone_code}`;

    return {
      label: `${option.label} ${phoneCodeValue}`,
      value: phoneCodeValue,
    };
  } else {
    return {label: option.label, value: option.value};
  }
};

const getPhoneOptionLabel = (option: Parameters<typeof getPhoneOption>[0]) => {
  const phoneOption = getPhoneOption(option);
  return phoneOption.label;
};

const checkIfOptionSelected = (
  option: PhoneCodeOption,
  selectValue: Options<PhoneCodeOption>,
) => {
  const selectedValue = selectValue?.[0];

  return option?.label === selectedValue?.label && option?.value === selectedValue?.value;
};

const PhoneCodesSelect = React.forwardRef<
  SelectRefType<LocationType | undefined>,
  PhoneCodesSelectProps
>(
  (
    {
      onChange,
      disabled,
      value,
      readOnly,
      error,
      searchValue: externalSearchValue,
      onSearchChange: externalOnSearchChange,
      onFocus,
      onBlur,
    },
    ref,
  ) => {
    const {phoneCodesAsOptions} = useFetchLocations();

    const [internalSearchValue, setInternalSearchValue] = React.useState('');
    const searchValue = externalSearchValue ?? internalSearchValue;
    const setSearchValue = externalOnSearchChange ?? setInternalSearchValue;

    const transformedValue = React.useMemo(() => {
      if (!value?.code) {
        return;
      }

      return {label: value?.name, value: value?.code};
    }, [value?.code, value?.name]);

    const handleCountryCodeChange = React.useCallback(
      (option: PhoneCodeOption | null) => {
        if (!option) return;
        const phoneOption = getPhoneOption(option);
        onChange({name: phoneOption.label, code: phoneOption.value});
        setSearchValue('');
      },
      [onChange, setSearchValue],
    );

    return (
      <SearchingSelect<LocationType | undefined>
        ref={ref}
        options={phoneCodesAsOptions}
        formatOptionLabel={getPhoneOptionLabel}
        isOptionSelected={checkIfOptionSelected}
        filterOption={customCountriesFilter}
        onChange={handleCountryCodeChange}
        value={transformedValue}
        disabled={disabled}
        className={cn(
          'phone-field__code-select',
          'static',
          '[&_.dropdown-select__menu]:top-12 [&_.dropdown-select__menu]:z-[5]',
          'kiosko:[&_.dropdown-select__menu]:top-[110px]',
          'kiosko:[&_.dropdown-select__menu]:z-[5]',
          'kiosko:[&_.select__search-icon]:h-12 kiosko:[&_.select__search-icon]:w-12',
          'kiosko:[&_.custom-icon]:h-12 kiosko:[&_.custom-icon]:w-12',
          'kiosko:[&_.select__control]:max-h-[78px]',
        )}
        inputValue={searchValue}
        onInputChange={newValue => setSearchValue(newValue)}
        onFocus={onFocus}
        onBlur={onBlur}
        trigger={(_, trigger) => (
          <button
            onClick={() => !disabled && !readOnly && trigger()}
            disabled={disabled}
            type="button"
            className={cn(
              'code-select',
              'absolute bottom-0 left-0 z-[1] flex h-full w-[60px] select-none items-center justify-between rounded-md bg-transparent px-[7px] pl-4 text-left',
              'font-[var(--regular-font-weight)] text-[var(--font-primary)] outline-none',
              'text-[length:var(--m-font-size)]',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4',
              error
                ? 'focus-visible:outline-[var(--status-danger)]'
                : 'focus-visible:outline-[var(--primary)]',
              !value?.code &&
                'text-[length:var(--s-font-size)] text-[var(--font-secondary)]',
              readOnly ? 'cursor-default' : 'cursor-pointer',
              disabled && 'cursor-not-allowed opacity-50',
              'kiosko:text-kiosko-s',
            )}
          >
            {value?.code ?? '+00'}
          </button>
        )}
      />
    );
  },
);

export {PhoneCodesSelect};
